"use client"

import type React from "react"
import { useEffect, useState } from "react"
import {
  FaSearch,
  FaPlus,
  FaEdit,
  FaMapMarkerAlt,
  FaFileExcel,
  FaFilter,
  FaCheckCircle,
  FaTimesCircle,
  FaEye,
  FaTrash,
  FaSort,
  FaSortUp,
  FaSortDown,
} from "react-icons/fa"
import { PaginationType, Pagination as PaginationUtils } from "../../utils/pagination.utils"
import { ExcelDownload } from "../../utils/excel.utils"
import { useAuth } from "../../provider/auth.provider"
import { AddDistrictOffcanvas } from "./district/add"
import axiosInstance from "../../libs/axios"
import { useDebouncedCallback } from "../../hooks/useDebounce"

interface State {
  id: number
  state_name: string
  circle_name?: string | null
  district_count: number
  is_active: boolean
  created_by: number
  suspended_by?: number | null
  suspended_at?: string | null
  created_at: string
  updated_at: string
}

interface StatesListProps {
  states: State[]
  pagination: PaginationType
  onSearch: (search: string) => void
  onFilterChange: (filter: string) => void
  activeFilter: string
  onPageChange: (page: number) => void
  onLimitChange: (limit: number) => void
  onAddState: () => void
  onEditState: (state: State) => void
  onDeleteState: (state: State) => void
  onToggleStatus: (state: State) => void
  loading?: boolean
  actionLoading?: boolean
  onSort?: (field: string, direction: "ASC" | "DESC") => void
  searchValue?: string
  sortState?: { field: string; direction: "ASC" | "DESC" }
}

export const StatesList: React.FC<StatesListProps> = ({
  states,
  pagination,
  onSearch,
  onFilterChange,
  activeFilter,
  onPageChange,
  onLimitChange,
  onAddState,
  onEditState,
  onDeleteState,
  onToggleStatus,
  loading = false,
  actionLoading = false,
  onSort,
  searchValue = "",
  sortState,
}) => {
  const [searchTerm, setSearchTerm] = useState("")
  const [showExcelModal, setShowExcelModal] = useState(false)
  const [showDistrict, setShowDistrict] = useState(false)
  const [selectedState, setSelectedState] = useState<State | null>(null)
  const { hasPermission } = useAuth()
  const [sortConfig, setSortConfig] = useState<{ field: string; direction: "ASC" | "DESC" }>({
    field: "created_at",
    direction: "DESC",
  })
  const [exporting, setExporting] = useState(false)
  const [exportData, setExportData] = useState<Record<string, any>[]>([])

  useEffect(() => {
    setSearchTerm(searchValue || "")
  }, [searchValue])

  useEffect(() => {
    if (sortState) {
      setSortConfig(sortState)
    }
  }, [sortState])

  const debouncedOnSearch = useDebouncedCallback(onSearch, 400)

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setSearchTerm(value)
    debouncedOnSearch(value)
  }

  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value
    onFilterChange(value)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  const getStatusBadge = (isActive: boolean) => {
    if (isActive) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <FaCheckCircle className="w-2 h-2" />
          Active
        </span>
      )
    } else {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <FaTimesCircle className="w-2 h-2" />
          Inactive
        </span>
      )
    }
  }

  const getSearchParam = () => {
    const activeSearch = searchTerm.trim() || searchValue.trim()
    return activeSearch
  }

  const formatDateTime = (dateString?: string | null) => {
    if (!dateString) return ""
    return new Date(dateString).toLocaleString()
  }

  const mapStateForExport = (state: State) => ({
    "Circle Name": state.state_name,
    "Short Circle Name": state.circle_name || "",
    Status: state.is_active ? "Active" : "Inactive",
    "District Count": state.district_count,
    "Created At": formatDateTime(state.created_at),
    "Updated At": formatDateTime(state.updated_at),
    "Suspended At": formatDateTime(state.suspended_at),
  })

  const buildExportParams = () => {
    const params: Record<string, any> = {}
    const searchParam = getSearchParam()
    if (searchParam) params.search = searchParam

    if (activeFilter && activeFilter !== "all") {
      params.is_active = activeFilter === "active"
    }

    if (sortConfig.field) {
      params.sort_by = sortConfig.field
      params.sort_order = sortConfig.direction
    }

    return params
  }

  const fetchExportData = async () => {
    try {
      setExporting(true)
      const params = buildExportParams()
      const limit = 100
      let page = 1
      let totalPages = 1
      const allStates: State[] = []

      do {
        const response = await axiosInstance.get("/state-circle/list", {
          params: {
            ...params,
            page,
            limit,
          },
        })

        const responseData = response.data?.data
        const pageStates: State[] = responseData?.states || []
        allStates.push(...pageStates)

        const paginationInfo = responseData?.pagination
        if (paginationInfo) {
          totalPages =
            paginationInfo.total_pages ??
            paginationInfo.totalPages ??
            (paginationInfo.total_count ? Math.ceil(paginationInfo.total_count / limit) : totalPages)
        } else if (pageStates.length < limit) {
          totalPages = page
        }

        page += 1
      } while (page <= totalPages)

      setExportData(allStates.map(mapStateForExport))
      setShowExcelModal(true)
    } catch (error) {
      console.error("Error exporting states:", error)
    } finally {
      setExporting(false)
    }
  }

  const handleSort = (field: string) => {
    const direction = sortConfig.field === field && sortConfig.direction === "ASC" ? "DESC" : "ASC"
    setSortConfig({ field, direction })
    onSort?.(field, direction)
  }

  const getSortIcon = (field: string) => {
    if (sortConfig.field !== field) return <FaSort className="w-3 h-3" />
    return sortConfig.direction === "ASC" ? <FaSortUp className="w-3 h-3" /> : <FaSortDown className="w-3 h-3" />
  }

  const filterOptions = [
    { value: "all", label: "All States" },
    { value: "active", label: "Active Only" },
    { value: "inactive", label: "Inactive Only" },
  ]

  return (
    <div className="bg-white shadow-sm rounded overflow-hidden border border-gray-200">
      {/* Header */}
      <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <h2 className="text-xl font-semibold text-gray-900">Circle Management</h2>
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <button
              onClick={fetchExportData}
              disabled={exporting}
              className="bg-green-600 hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed text-white px-4 py-2 rounded flex items-center justify-center gap-2 transition-colors text-sm font-medium shadow-sm"
            >
              <FaFileExcel className="w-4 h-4" />
              {exporting ? "Preparing..." : "Export Excel"}
            </button>
            {hasPermission("StateCircle", "create") && (
              <button
                onClick={onAddState}
                className="bg-blue-800 hover:bg-blue-950 text-white px-4 py-2 rounded flex items-center justify-center gap-2 transition-colors text-sm font-medium shadow-sm"
              >
                <FaPlus className="w-4 h-4" />
                Add New Circle
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gray-50/30">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Search */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Search Circles</label>
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-3 h-3" />
              <input
                type="text"
                placeholder="Search by circle name"
                value={searchTerm}
                onChange={handleSearchChange}
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded  focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
            </div>
          </div>

          {/* Filter */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Filter by Status</label>
            <div className="relative">
              <FaFilter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-3 h-3" />
              <select
                value={activeFilter}
                onChange={handleFilterChange}
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded  focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm appearance-none bg-white"
              >
                {filterOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[150px]">
                Actions
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[180px] cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => handleSort("state_name")}
              >
                <div className="flex items-center gap-1">
                  Circle Name
                  {getSortIcon("state_name")}
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[150px] cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => handleSort("circle_name")}
              >
                <div className="flex items-center gap-1">
                  Short Circle Name
                  {getSortIcon("circle_name")}
                </div>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[120px]">
                Districts
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[120px] cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => handleSort("is_active")}
              >
                <div className="flex items-center gap-1">
                  Status
                  {getSortIcon("is_active")}
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[150px] cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => handleSort("created_at")}
              >
                <div className="flex items-center gap-1">
                  Created At
                  {getSortIcon("created_at")}
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[150px] cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => handleSort("updated_at")}
              >
                <div className="flex items-center gap-1">
                  Updated At
                  {getSortIcon("updated_at")}
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[150px] cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => handleSort("suspended_at")}
              >
                <div className="flex items-center gap-1">
                  Suspended At
                  {getSortIcon("suspended_at")}
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={7} className="px-4 sm:px-6 py-12 text-center">
                  <div className="flex flex-col items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-3"></div>
                    <span className="text-sm text-gray-600">Loading states...</span>
                  </div>
                </td>
              </tr>
            ) : states.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 sm:px-6 py-12 text-center">
                  <div className="flex flex-col items-center justify-center">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                      <FaMapMarkerAlt className="w-6 h-6 text-gray-400" />
                    </div>
                    <p className="text-sm text-gray-500">No states found</p>
                    <p className="text-xs text-gray-400 mt-1">Try adjusting your search or create a new state</p>
                  </div>
                </td>
              </tr>
            ) : (
              states.map((state) => (
                <tr key={state.id} className="hover:bg-gray-50 transition-colors">
                  {/* Actions */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-1">
                      {hasPermission("StateCircle", "edit") && (
                        <button
                          onClick={() => onEditState(state)}
                          disabled={actionLoading}
                          className={`text-blue-600 hover:text-blue-800 p-2 hover:bg-blue-50 rounded transition-colors ${actionLoading ? "opacity-50 cursor-not-allowed" : ""
                            }`}
                          title="Edit state"
                        >
                          <FaEdit className="w-4 h-4" />
                        </button>
                      )}
                      {/* {hasPermission('StateCircle', 'delete') && (
                        <button
                          onClick={() => onDeleteState(state)}
                          disabled={actionLoading}
                          className={`text-red-600 hover:text-red-800 p-2 hover:bg-red-50 rounded transition-colors ${actionLoading ? 'opacity-50 cursor-not-allowed' : ''
                            }`}
                          title="Delete state"
                        >
                          <FaTrash className="w-4 h-4" />
                        </button>
                      )} */}
                    </div>
                  </td>

                  {/* Circle Name */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-semibold text-gray-900">{state.state_name}</span>
                    </div>
                  </td>

                  {/* Short Circle Name */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">
                      {state.circle_name ? (
                        <span className="font-medium">{state.circle_name}</span>
                      ) : (
                        <span className="text-gray-400 italic">N/A</span>
                      )}
                    </span>
                  </td>

                  <td className="px-4 py-4 whitespace-nowrap">
                    <button
                      className="group flex items-center gap-2 px-2 py-0.5 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 rounded-full hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-sm hover:shadow-md"
                      onClick={() => {
                        setShowDistrict(true)
                        setSelectedState(state)
                      }}
                    >
                      <FaEye className="w-3.5 h-3.5" />
                      <span>View ({state.district_count})</span>
                    </button>
                  </td>

                  {/* Status */}
                  <td className="px-4 py-4 whitespace-nowrap">{getStatusBadge(state.is_active)}</td>

                  {/* Created At */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">{formatDate(state.created_at)}</span>
                    </div>
                  </td>

                  {/* Updated At */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">{formatDate(state.updated_at)}</span>
                    </div>
                  </td>

                  {/* Suspended At */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    {state.suspended_at ? (
                      <div className="flex items-center gap-2">
                        <FaTimesCircle className="w-3 h-3 text-red-400" />
                        <span className="text-sm text-red-600">{formatDate(state.suspended_at)}</span>
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400 italic">Not suspended</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <PaginationUtils pagination={pagination as any} onPageChange={onPageChange} onLimitChange={onLimitChange} />

      <AddDistrictOffcanvas
        isOpen={showDistrict}
        onClose={() => setShowDistrict(false)}
        stateCircle={selectedState as State}
      />

      {/* Excel Download Modal */}
      <ExcelDownload
        isOpen={showExcelModal}
        onClose={() => setShowExcelModal(false)}
        data={exportData}
        filename="state_circles"
        title="Export States Data"
        description="Download the filtered state & circle records with all available details"
      />
    </div>
  )
}
